import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, Sparkles, Lock } from "lucide-react";
import { askCoachesHelper } from "../../lib/api";

const REF = /\[\[([CRNWS]:[0-9a-f]{24}(?::\d{4}-\d{2}-\d{2})?)\]\]/g;

function bold(text, key) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={`${key}-${i}`}>{p.slice(2, -2)}</strong> : p));
}

// Renders answer text: inline [[ref]] citations become links to the exact record.
function Answer({ text, sources }) {
  const byRef = Object.fromEntries((sources || []).map((s) => [s.ref, s]));
  return (
    <div className="space-y-2 text-brand-ink">
      {text.split("\n").map((line, li) => {
        const parts = [];
        let last = 0;
        line.replace(REF, (m, ref, idx) => {
          parts.push(...bold(line.slice(last, idx), `${li}-${idx}`));
          const s = byRef[ref];
          parts.push(s ? <Link key={`${li}-r${idx}`} to={s.url} className="text-[#8a6234] underline text-xs" data-testid={`helper-inline-ref-${ref}`}>[{s.label}]</Link> : null);
          last = idx + m.length;
          return m;
        });
        parts.push(...bold(line.slice(last), `${li}-end`));
        return <p key={li} className={line.trim().startsWith("- ") ? "pl-3" : ""}>{parts}</p>;
      })}
    </div>
  );
}

export function CoachesHelper() {
  const [turns, setTurns] = useState([]); // session-only, never persisted
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ask = async (e) => {
    e.preventDefault();
    const question = q.trim();
    if (!question || busy) return;
    setBusy(true); setErr("");
    const history = turns.map((t) => ({ role: t.role, content: t.content }));
    setTurns((t) => [...t, { role: "user", content: question }]);
    setQ("");
    try {
      const res = await askCoachesHelper(question, history);
      setTurns((t) => [...t, { role: "assistant", content: res.answer, sources: res.sources }]);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "The Coaches Helper couldn't answer right now.");
      setTurns((t) => t.slice(0, -1));
      setQ(question);
    } finally { setBusy(false); }
  };

  return (
    <section className="surface-card mt-10 p-6 md:p-8" data-testid="coaches-helper">
      <div className="label-eyebrow flex items-center gap-3"><Sparkles size={14} strokeWidth={1.8} /> The Coaches Helper</div>
      <p className="mt-2 text-sm text-brand-ink-muted flex items-center gap-2"><Lock size={12} /> Ask anything about your stored client data. Read-only: it can never change anything.</p>
      <div className="mt-5 space-y-4 max-h-[520px] overflow-y-auto" data-testid="helper-conversation">
        {turns.map((t, i) => (
          <div key={i} className={t.role === "user" ? "text-right" : ""} data-testid={`helper-turn-${i}`}>
            {t.role === "user" ? (
              <span className="inline-block rounded-2xl bg-brand-ink text-white px-4 py-2 text-sm">{t.content}</span>
            ) : (
              <div className="rounded-2xl border border-brand-border p-4" data-testid={`helper-answer-${i}`}>
                <Answer text={t.content} sources={t.sources} />
                {t.sources?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`helper-sources-${i}`}>
                    {t.sources.map((s) => (
                      <Link key={s.ref} to={s.url} className="rounded-full border border-[#C8965A] px-3 py-1 text-xs text-[#8a6234] hover:bg-[#C8965A]/10" data-testid={`helper-source-${s.ref}`}>{s.label}</Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {busy && <p className="text-sm text-brand-ink-muted inline-flex items-center gap-2" data-testid="helper-thinking"><Loader2 size={14} className="animate-spin" /> Looking through your client data…</p>}
      </div>
      {err && <p className="mt-3 text-sm text-red-700" data-testid="helper-error">{err}</p>}
      <form onSubmit={ask} className="mt-4 flex gap-3">
        <input className="input-warm flex-1" placeholder="e.g. Which clients have missed more than one Weekly Check-In?" value={q} onChange={(e) => setQ(e.target.value)} data-testid="helper-input" />
        <button type="submit" disabled={busy || !q.trim()} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="helper-send-btn"><Send size={16} /> Ask</button>
      </form>
      {turns.length > 0 && (
        <button type="button" onClick={() => setTurns([])} className="mt-3 text-xs uppercase tracking-[0.2em] font-bold text-brand-ink-muted hover:text-brand-ink" data-testid="helper-clear-btn">New conversation</button>
      )}
    </section>
  );
}

export default CoachesHelper;
